'use client';

import Breadcrumbs from 'components/breadcrumbs';
import GridContainer from 'components/containers/grid-container';
import PageSection from 'components/containers/page-section';
import SimpleSelect from 'components/controls/simple-select';
import MinimalHeader from 'components/headers/minimal-header';
import ExhibitionCard from 'components/cards/exhibition-card';
import { usePathname, useRouter } from 'src/navigation';
import { type IExhibitionListing } from 'queries/ExhibitionListingQuery';
import { useMemo } from 'react';
import styles from './past-exhibitions.module.scss';
import Pagination from 'components/controls/pagination';

interface IProps {
	currentYear: number;
	yearsWithExhibitions: number[];
	exhibitions: IExhibitionListing[];
}

type yearOption = {
	isDisabled: boolean;
	name: string;
	value: string;
};

const toOption = (year: number): yearOption => {
	return { name: `${year}`, value: `${year}`, isDisabled: false };
};

const toOptions = (years: number[]) => {
	return years.map(toOption);
};

const PastExhibitionsApp = ({
	currentYear,
	yearsWithExhibitions,
	exhibitions,
}: IProps) => {
	const pathname = usePathname();
	const router = useRouter();

	const years = useMemo(
		() => toOptions(yearsWithExhibitions),
		[yearsWithExhibitions],
	);
	const isFirst = currentYear == yearsWithExhibitions[0];
	const isLast =
		currentYear == yearsWithExhibitions[yearsWithExhibitions.length - 1];

	const handleSelectChange = (event) => {
		const year = event.target.value;
		year == years[0].value
			? router.push(`${pathname}`, { scroll: true })
			: router.push(`${pathname}?${new URLSearchParams({ year } as any)}`, {
					scroll: true,
				});
	};

	const backButton = toOption(currentYear - 1);
	const forwardButton = toOption(currentYear + 1);

	const paginationProps = {
		onClick: handleSelectChange,
		backButton: !isLast ? backButton : { ...backButton, hidden: true },
		forwardButton: !isFirst
			? forwardButton
			: { ...forwardButton, hidden: true },
	};

	return (
		<main>
			<Breadcrumbs lastSegmentOverride="Past Exhibitions" />
			<MinimalHeader heading="Past Exhibitions" />
			<PageSection>
				<div className={styles.resultsCountSection}>
					<span className={styles.sortByTitle}>Select year</span>
					<SimpleSelect
						id="year"
						initialValue={`${currentYear}`}
						options={years}
						handleChange={handleSelectChange}
					/>
				</div>
				<GridContainer columns={3}>
					{!!exhibitions?.length &&
						exhibitions.map((exhibition) => {
							return (
								<ExhibitionCard
									key={exhibition._id}
									exhibition={exhibition}
									cardWidth="100%"
									cardMaxWidth="100%"
								/>
							);
						})}
				</GridContainer>
			</PageSection>
			<PageSection>
				<div className={styles.paginationContainer}>
					<Pagination {...paginationProps} />
				</div>
			</PageSection>
		</main>
	);
};

export default PastExhibitionsApp;
